import type { AppConfig, ConfigReader as ConfigReaderType } from '@backstage/config';
import { ConfigReader } from '@backstage/config';
import type { ConfigApi } from '@backstage/frontend-plugin-api';
import type { JsonObject, JsonValue } from '@backstage/types';
import { mergeAppConfigs } from '@ns/config-merge';

/**
 * `createApp`'s config-injection places the runtime config on `window.__APP_CONFIG__`.
 * The built-in loader reads it untyped; we declare it so the loader stays cast-free.
 */
declare global {
  interface Window {
    __APP_CONFIG__?: JsonObject;
  }
}

/**
 * The sentinel `defaultConfigLoaderSync` uses for the injected-runtime-config argument.
 * Backstage's webpack build replaces the LITERAL `'__APP_INJECTED_RUNTIME_CONFIG__'`
 * with the real runtime JSON at build time. To detect "nothing was injected" without
 * this comparison string itself being replaced, the built-in loader compares against
 * the case-folded lowercase form upper-cased back, reproduced byte-faithfully below.
 */
const RUNTIME_CONFIG_SENTINEL = '__APP_INJECTED_RUNTIME_CONFIG__';

/**
 * Reproduce the non-exported `defaultConfigLoaderSync` from `@backstage/core-app-api`
 * byte-faithfully: collect the frontend config source branches in the exact order and
 * with the exact XOR/guard semantics the framework uses.
 *
 * Branch structure (verified against
 * `@backstage/core-app-api/dist/app/defaultConfigLoader.esm.js`):
 * - A: `process.env.APP_CONFIG` (webpack build-time inject, the dev path). Always
 *   evaluated; must already be an `AppConfig[]`.
 * - B XOR C: `<script type="backstage.io/config">` tags, else-if the injected runtime
 *   config JSON. These are MUTUALLY EXCLUSIVE (`else if`): when script tags exist, the
 *   injected-runtime branch never runs.
 * - The injected-runtime branch is guarded by a case-fold sentinel check so the loader
 *   does not try to `JSON.parse` the literal sentinel when nothing was injected.
 * - D: `window.__APP_CONFIG__` (optional).
 */
function reconstructFrontendConfigs(
  runtimeConfigJson = RUNTIME_CONFIG_SENTINEL,
): AppConfig[] {
  const configs: AppConfig[] = [];

  // A: APP_CONFIG env. Already an AppConfig[] (webpack injects it as an array).
  const staticConfig = process.env.APP_CONFIG;
  if (staticConfig) {
    if (!Array.isArray(staticConfig)) {
      throw new Error('Static configuration has invalid format');
    }
    configs.push(...staticConfig);
  }

  // B XOR C: script tags, else-if the injected-runtime config.
  const configScripts = document.querySelectorAll(
    'script[type="backstage.io/config"]',
  );
  if (configScripts.length > 0) {
    for (const el of configScripts) {
      try {
        const content = el.textContent;
        if (!content) {
          throw new Error('tag is empty');
        }
        let data: unknown;
        try {
          data = JSON.parse(content);
        } catch (error) {
          throw new Error(`failed to parse config; ${error}`);
        }
        if (!Array.isArray(data)) {
          throw new Error('data is not an array');
        }
        configs.push(...data);
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        throw new Error(`Failed to load config from script tag, ${message}`);
      }
    }
  } else if (
    // Case-fold sentinel guard, reproduced byte-faithfully. The literal
    // `__APP_INJECTED_RUNTIME_CONFIG__` is replaced at build time; comparing against
    // the upper-cased lowercase form keeps THIS comparison string from also being
    // replaced, so an un-injected sentinel is detected instead of parsed as JSON.
    runtimeConfigJson !== '__app_injected_runtime_config__'.toLocaleUpperCase('en-US')
  ) {
    try {
      const data: JsonValue = JSON.parse(runtimeConfigJson);
      if (Array.isArray(data)) {
        // The injected-runtime array is already an AppConfig[] ({context, data} items).
        for (const item of data) {
          if (!isAppConfig(item)) {
            throw new Error('Runtime configuration item is not an AppConfig');
          }
          configs.push(item);
        }
      } else if (isJsonObject(data)) {
        configs.push({ context: 'env', data });
      } else {
        throw new Error('Runtime configuration is not an object');
      }
    } catch (error) {
      throw new Error(`Failed to load runtime configuration, ${error}`);
    }
  }

  // D: window.__APP_CONFIG__ (optional).
  const windowAppConfig = window.__APP_CONFIG__;
  if (windowAppConfig) {
    configs.push({ context: 'window', data: windowAppConfig });
  }

  return configs;
}

/** True for a plain JSON object (not null, not an array). */
function isJsonObject(value: JsonValue): value is JsonObject {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * True when `value` is an {@link AppConfig}: a `{ context: string; data: JsonObject }`
 * object. Used to narrow the parsed injected-runtime array without a cast.
 */
function isAppConfig(value: JsonValue): value is AppConfig {
  if (!isJsonObject(value)) {
    return false;
  }
  const { context, data } = value;
  return typeof context === 'string' && data !== undefined && isJsonObject(data);
}

/** Rewrite `fullUrl`'s protocol/host/port to the current document origin, no trailing slash. */
function createLocalBaseUrl(fullUrl: string): string {
  const url = new URL(fullUrl);
  url.protocol = document.location.protocol;
  url.hostname = document.location.hostname;
  url.port = document.location.port;
  return url.toString().replace(/\/$/, '');
}

/**
 * Reproduce the non-exported `overrideBaseUrlConfigs` byte-faithfully: rewrite
 * `app.baseUrl` / `backend.baseUrl` to the local origin, and CONCAT a
 * `context: 'relative-resolver'` config carrying the rewritten URLs ONLY when a rewrite
 * actually changed a URL (verified against
 * `@backstage/core-app-api/dist/app/overrideBaseUrlConfigs.esm.js`).
 */
function overrideBaseUrlConfigs(inputConfigs: AppConfig[]): AppConfig[] {
  const urlConfigReader = ConfigReader.fromConfigs(inputConfigs);
  const appBaseUrl = urlConfigReader.getOptionalString('app.baseUrl');
  const backendBaseUrl = urlConfigReader.getOptionalString('backend.baseUrl');

  let newBackendBaseUrl: string | undefined;
  let newAppBaseUrl: string | undefined;

  if (appBaseUrl && backendBaseUrl) {
    const appOrigin = new URL(appBaseUrl).origin;
    const backendOrigin = new URL(backendBaseUrl).origin;
    if (appOrigin === backendOrigin) {
      const maybeNewBackendBaseUrl = createLocalBaseUrl(backendBaseUrl);
      if (backendBaseUrl !== maybeNewBackendBaseUrl) {
        newBackendBaseUrl = maybeNewBackendBaseUrl;
      }
    }
  }

  if (appBaseUrl) {
    const maybeNewAppBaseUrl = createLocalBaseUrl(appBaseUrl);
    if (appBaseUrl !== maybeNewAppBaseUrl) {
      newAppBaseUrl = maybeNewAppBaseUrl;
    }
  }

  if (!newAppBaseUrl && !newBackendBaseUrl) {
    return inputConfigs;
  }

  // Build the relative-resolver data with only the rewritten keys present. The upstream
  // uses `app: newAppBaseUrl && {baseUrl}` (falsey branches drop out via ConfigReader);
  // constructing the object conditionally is the type-safe equivalent.
  const app = newAppBaseUrl ? { app: { baseUrl: newAppBaseUrl } } : undefined;
  const backend = newBackendBaseUrl
    ? { backend: { baseUrl: newBackendBaseUrl } }
    : undefined;

  return inputConfigs.concat({
    context: 'relative-resolver',
    data: { ...app, ...backend },
  });
}

/**
 * A merge-only, dev-parity config loader for `createApp`.
 *
 * In PRODUCTION the frontend receives the already-merged config from the backend via the
 * `plugin-app-backend` script-tag, so this loader must be a NO-OP over an already-merged
 * config (idempotency, the hard requirement). In DEV (`yarn dev`, webpack-dev-server)
 * the frontend loads its own config with no backend merge; this loader applies the same
 * per-item `app.extensions` merge the prod backend applies, so `yarn dev` shows the merge.
 *
 * It reconstructs the frontend source branches, applies the base-URL rewrite, folds with
 * `mergeAppConfigs` (the pure per-item fold from `@ns/config-merge`, NO governance,
 * governance is the backend's job before script-tag injection), and returns the
 * `{ config }` shape `createApp` requires. It returns `ConfigReader.fromConfigs(...)`,
 * NOT the raw `AppConfig[]`, the seam expects a built `ConfigApi`.
 */
export async function nsConfigLoader(): Promise<{ config: ConfigApi }> {
  const base = reconstructFrontendConfigs();
  const withBaseUrl = overrideBaseUrlConfigs(base);
  const merged = mergeAppConfigs(withBaseUrl);
  const config: ConfigReaderType = ConfigReader.fromConfigs(merged);
  return { config };
}
